import React, { useState, useRef, useEffect } from 'react';
import '../styles/PixelGarden.css';
import gardenService from '../services/gardenService';
import plantDataService from '../services/plantDataService';
import placeholderImg from '../assets/pixel-art/seeding.png';
import trashBinImg from '../assets/pixel-art/bin.png'
import ImageModal from '../components/ImageModal';
import StageImageThumbnail from '../components/StageImageThumbnail';
import 'intro.js/introjs.css';
import introJs from 'intro.js';
import PlantsSummary from './PlantsSummary';
import GardenEcoSummary from './EcoSummaryCard';
import ecoReportCacheService from '../services/ecoReportCacheService';


const binImg = trashBinImg;

const PixelGarden = () => {
  // Get initial plants from service
  const [plants, setPlants] = useState(gardenService.getPlants());

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [draggedPlant, setDraggedPlant] = useState(null);
  const [showPlantMenu, setShowPlantMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showReminderCalendar, setShowReminderCalendar] = useState(false);
  const [hasReminder, setHasReminder] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlantType, setSelectedPlantType] = useState(null);
  const [plantDatabase, setPlantDatabase] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [plantStages, setPlantStages] = useState([]);
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const [selectedTask, setSelectedTask] = useState('water');
  const [customTaskText, setCustomTaskText] = useState('');
  const [daysFromNow, setDaysFromNow] = useState(3); // Default to 3 days from now
  const [reminderHour, setReminderHour] = useState(9); // Default to 9 AM
  const [reminderMinute, setReminderMinute] = useState(0);
  const [reminders, setReminders] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [plantToDelete, setPlantToDelete] = useState(null);
  const [isEditingStage, setIsEditingStage] = useState(false);
  const [isEditingHealth, setIsEditingHealth] = useState(false);
  // Tour state
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourPlantAdded, setTourPlantAdded] = useState(false);
  const [demoPlantAdded, setDemoPlantAdded] = useState(false);
  const [gardenDimensions, setGardenDimensions] = useState({ width: 0, height: 0 });
  const [previousDimensions, setPreviousDimensions] = useState({ width: 0, height: 0 });

  const PLANT_HEALTH_STATUSES = ['healthy', 'stressed', 'diseased', 'pest-infested', 'wilting'];

  const gardenRef = useRef(null);
  const binRef = useRef(null);
  const searchInputRef = useRef(null);
  const tourRef = useRef(null);
  const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';
  // const API_BASE_URL = "http://localhost:5000/api";

  // Plant types from service
  const plantTypes = gardenService.plantTypes;

  // Calculate actual date from the slider value
  const getDateFromSlider = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  // Get suggested number of days based on plant type and task
  const getSuggestedDays = () => {
    if (!selectedPlant) return 3;

    // Default suggestion is 3 days
    let daysToAdd = 3;

    // Adjust based on plant type, task and current stage
    if (selectedPlant.plantData) {
      if (selectedTask === 'water') {
        if (selectedPlant.plantData.watering_frequency_level === 'High') {
          daysToAdd = 2; // Water more frequently for high-need plants
        } else if (selectedPlant.plantData.watering_frequency_level === 'Low') {
          daysToAdd = 5; // Water less frequently for drought-tolerant plants
        }
      } else if (selectedTask === 'fertilise') {
        daysToAdd = 14; // Fertilise approximately every two weeks
      } else if (selectedTask === 'prune') {
        daysToAdd = 30; // Pruning is less frequent
      }

      // Further adjust based on growth stage
      if (selectedPlant.stage && selectedPlant.stage.toLowerCase().includes('seedling')) {
        if (selectedTask === 'water') {
          daysToAdd = Math.max(1, daysToAdd - 1); // Seedlings need more frequent watering
        }
      }
    }

    return daysToAdd;
  };

  // Format time for display (12-hour format with AM/PM)
  const formatTimeDisplay = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:00 ${period}`;
  };

  // Format date for storage
  const formatDateKey = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // Format date for display
  const formatDateDisplay = (date) => {
    if (!date) return '';

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    // Check if date is today
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }

    // Check if date is tomorrow
    if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }

    // Otherwise return the date
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-AU', options);
  };

  // function to allow user change the displayed stage for plant
  const handleStageChange = (newStage) => {
    if (!selectedPlant) return;

    // Get the stages and find the new stage's position
    const stages = getGrowthStages();
    const newStageObj = stages.find(s => s.name === newStage || s.stage_name === newStage);

    if (!newStageObj) {
      console.error(`Stage "${newStage}" not found in available stages`);
      return;
    }

    // Determine new progress value based on stage position
    let newProgress = selectedPlant.progress; // Default to current progress

    // If we're changing stages, update the progress to match the stage position
    if (newStageObj.position !== undefined) {
      // Use the stage position as the progress percentage
      newProgress = Math.round(newStageObj.position);
      console.log(`Updated progress to ${newProgress}% based on stage position at ${newStageObj.position}`);
    }

    // Create the updated plant object with new stage and progress
    const updatedPlant = {
      ...selectedPlant,
      stage: newStage,
      progress: newProgress
    };

    // Update plant in service
    gardenService.updatePlant(updatedPlant);

    ecoReportCacheService.invalidateCache();


    // Update local state
    setSelectedPlant(updatedPlant);
    setIsEditingStage(false);
  };


  // Handle hour change from slider
  const handleHourChange = (e) => {
    setReminderHour(parseInt(e.target.value, 10));
  };

  // Apply recommended settings
  const useRecommendedSettings = () => {
    // Set recommended days based on plant type and task
    const recommendedDays = getSuggestedDays();
    setDaysFromNow(recommendedDays);

    // Set recommended time (9 AM for morning tasks, 6 PM for evening)
    if (['water', 'fertilise'].includes(selectedTask)) {
      setReminderHour(9); // Morning is better for watering and fertilizing
    } else {
      setReminderHour(18); // Evening might be better for other tasks
    }

    setReminderMinute(0);
  };

  // Set a reminder
  const handleSetReminder = () => {
    if (!selectedPlant) return;

    // Get the task name (either predefined or custom)
    const taskName = selectedTask === 'custom' ? customTaskText : selectedTask;

    if (selectedTask === 'custom' && !customTaskText.trim()) {
      alert('Please enter a custom task description');
      return;
    }

    // Calculate the reminder date from the slider value
    const reminderDate = getDateFromSlider(daysFromNow);

    reminderDate.setHours(reminderHour, reminderMinute, 0, 0);

    // Format the date as YYYY-MM-DD for consistent comparison
    const dateKey = formatDateKey(reminderDate);

    // Define task icon based on selected task
    const taskIcon =
      selectedTask === 'water' ? '💧' :
        selectedTask === 'fertilise' ? '🌱' :
          selectedTask === 'prune' ? '✂️' : '⚙️';

    // Create reminder object
    const reminder = {
      id: Date.now(),
      plantId: selectedPlant.id,
      plantName: selectedPlant.type,
      task: taskName,
      taskType: selectedTask,
      date: reminderDate.toISOString(), // Store as ISO string for better serialization
      dateKey: dateKey, // Add dateKey field for easier comparison
      icon: taskIcon,
      reminderTime: reminderHour * 60 + reminderMinute
    };

    // Add reminder to state
    const updatedReminders = [...reminders, reminder];
    setReminders(updatedReminders);

    // Save to localStorage - IMPORTANT: Use a different key to avoid conflicts
    gardenService.addReminder(reminder);

    ecoReportCacheService.invalidateCache();

    console.log('Reminder saved:', reminder);
    console.log('All reminders:', updatedReminders);

    // Update plant with reminder date
    const updatedPlant = {
      ...selectedPlant,
      reminderDate: dateKey,
      nextTask: taskName,
      reminderTime: reminderHour * 60 + reminderMinute
    };

    // Update plant in service
    gardenService.updatePlant(updatedPlant);

    // Show success message
    alert(`Reminder set for ${taskName} on ${reminderDate.toLocaleDateString('en-AU')} at ${formatTimeDisplay(reminderHour)}`);


    setHasReminder(true);
    setShowReminderCalendar(false);
  };


  // Clear all reminders for selected plant
  const handleClearReminders = () => {
    if (!selectedPlant) return;

    // Use service to clear reminders
    gardenService.clearPlantReminders(selectedPlant.id);

    // Update local state
    setReminders(gardenService.getReminders());

    // Update plant to remove reminder
    const updatedPlant = {
      ...selectedPlant,
      reminderDate: null,
      nextTask: null
    };

    // Update plant in service
    gardenService.updatePlant(updatedPlant);

    ecoReportCacheService.invalidateCache();

    // Show confirmation
    alert('All reminders cleared for this plant');

    setHasReminder(false);
    setShowReminderCalendar(false);
  };

  // Load reminders from localStorage on component mount
  // Add this to your useEffect hooks
  useEffect(() => {
    const loadedReminders = gardenService.getReminders();
    console.log('Loaded reminders in PixelGarden:', loadedReminders);
    setReminders(loadedReminders);
    // console.log("icon url  ",selectedPlant.icon_image_url);


    // Check if selected plant has any reminders
    if (selectedPlant) {
      const hasAnyReminder = loadedReminders.some(
        reminder => reminder.plantId === selectedPlant.id
      );
      setHasReminder(hasAnyReminder);
    }

    // Listen for storage events (from other tabs or components)
    const handleStorageChange = (e) => {
      if (e.key === 'plantTaskReminders' || !e.key) {
        setReminders(gardenService.getReminders());

        // Also check if this affects the current selected plant
        if (selectedPlant) {
          const updatedReminders = gardenService.getReminders();
          const hasCurrentReminder = updatedReminders.some(
            reminder => reminder.plantId === selectedPlant.id
          );
          setHasReminder(hasCurrentReminder);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [selectedPlant?.id]);

  // Define growth stages with their positions and care instructions
  // This function will now be used as a fallback if API fails
  const getDefaultGrowthStages = () => {
    return [
      {
        name: "seedling",
        position: 0,
        description: "Young plant just sprouted from seed",
        careInstructions: "Keep soil moist but not soggy. Place in bright indirect light. Protect from strong winds and pests."
      },
      {
        name: "growth",
        position: 33,
        description: "Growing leaves and establishing roots",
        careInstructions: "Water regularly when soil is dry 1 inch below surface. Add support stakes if plant is getting tall. Fertilise every 2 weeks."
      },
      {
        name: "flowering",
        position: 66,
        description: "Developing flowers that will become fruit",
        careInstructions: "Ensure consistent watering. Tap flowers gently to aid pollination. Reduce nitrogen fertiliser and increase potassium."
      },
      {
        name: "harvest",
        position: 100,
        description: "Plant is ready to harvest",
        careInstructions: "Harvest when fruits are firm and have developed full color. Cut rather than pull from vine. Water less frequently."
      }
    ];
  };

  // Function to get stages from API or use default stages
  const getGrowthStages = () => {
    if (plantStages.length > 0) {
      // Map API data to our required format with consistent naming
      return plantStages.map((stage, index) => {
        // Calculate position based on sequence_order
        const position = ((stage.sequence_order - 1) / (plantStages.length - 1)) * 100;

        // Use the exact stage name from the API
        const stageName = stage.stage_name;

        // Default care instructions based on stage names
        let careInstructions = "Regular watering and care recommended.";
        if (stageName.toLowerCase().includes("germination")) {
          careInstructions = "Keep soil consistently moist. Maintain warm temperature.";
        } else if (stageName.toLowerCase().includes("seedling")) {
          careInstructions = "Keep soil moist but not soggy. Place in bright indirect light.";
        } else if (stageName.toLowerCase().includes("vegetative")) {
          careInstructions = "Water regularly. Add support stakes if needed. Fertilise every 2 weeks.";
        } else if (stageName.toLowerCase().includes("flowering")) {
          careInstructions = "Ensure consistent watering. Aid pollination. Adjust fertiliser.";
        } else if (stageName.toLowerCase().includes("senescence")) {
          careInstructions = "Reduce watering frequency. Remove dead leaves and stems.";
        }

        return {
          name: stageName, // Use exact API stage name
          position: position,
          description: `${stageName} stage of plant development`,
          careInstructions: careInstructions,
          image_url: stage.image_url
        };
      });
    }

    // Fallback to default stages if API data is not available
    return getDefaultGrowthStages();
  };

  // Fetch plant stages from the backend
  const fetchPlantStages = async (plantName) => {
    if (!plantName) return;

    setIsLoadingStages(true);
    try {
      const response = await fetch(`${API_BASE_URL}/database/plantStage/${plantName}`);
      // const response = await fetch(`http://localhost:5000/api/database/plantStage/${plantName}`);
      const data = await response.json();

      if (data.status === 'success' && data.stageData && data.stageData.length > 0) {
        // Sort stages by sequence_order to ensure proper display order
        const sortedStages = data.stageData.sort((a, b) => a.sequence_order - b.sequence_order);
        setPlantStages(sortedStages);
      } else {
        console.log("No stage data available for this plant, using defaults");
        setPlantStages([]);
      }
    } catch (error) {
      console.error("Error fetching plant stages:", error);
      setPlantStages([]);
    } finally {
      setIsLoadingStages(false);
    }
  };

  // Calculate days since planting
  const calculateDaysSincePlanting = (startDate) => {
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = Math.abs(today - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate time-based progress
  const calculateTimeProgress = (startDate, maxDaysToHarvest) => {
    const elapsed = calculateDaysSincePlanting(startDate);
    const total = maxDaysToHarvest;
    return Math.min(100, Math.round((elapsed / total) * 100));
  };

  // Find current stage index and get next stage info
  const getCurrentStageInfo = (currentStage) => {
    const stages = getGrowthStages();
    if (!currentStage || currentStage === '') {
      // If no stage is provided, default to first stage
      return {
        currentStageIndex: 0,
        nextStage: stages.length > 1 ? stages[1] : null
      };
    }

    // First try to find exact match (case insensitive)
    let currentStageIndex = stages.findIndex(s =>
      s.name.toLowerCase() === currentStage.toLowerCase()
    );

    // If exact match not found, try to find partial match
    if (currentStageIndex === -1) {
      currentStageIndex = stages.findIndex(s =>
        s.name.toLowerCase().includes(currentStage.toLowerCase()) ||
        currentStage.toLowerCase().includes(s.name.toLowerCase())
      );
    }

    // If still not found, use the first stage
    if (currentStageIndex === -1) {
      console.log(`Stage '${currentStage}' not found in available stages, defaulting to first stage`);
      currentStageIndex = 0;
    }

    const nextStage = currentStageIndex < stages.length - 1 ? stages[currentStageIndex + 1] : null;

    return {
      currentStageIndex,
      nextStage
    };
  };

  // Calculate days to next stage
  const calculateDaysToNextStage = (startDate, currentStage, maxDaysToHarvest) => {
    const { currentStageIndex, nextStage } = getCurrentStageInfo(currentStage);

    // Safety checks
    if (!nextStage) return 0; // No next stage (already at harvest)

    // Get stages and check if they exist
    const stages = getGrowthStages();
    if (!stages || stages.length === 0 || currentStageIndex < 0 || currentStageIndex >= stages.length) {
      console.log("Invalid stage data or index in calculateDaysToNextStage", { currentStage, currentStageIndex });
      return 0;
    }

    // Use approximate calculation based on progress percentage and stage positions
    const elapsed = calculateDaysSincePlanting(startDate);
    const progress = (elapsed / maxDaysToHarvest) * 100;

    // Safely access current position with fallback
    const currentPosition = stages[currentStageIndex]?.position || 0;
    const nextPosition = nextStage?.position || 100;
    const positionDifference = nextPosition - currentPosition;

    // Avoid division by zero
    if (positionDifference <= 0) {
      return 0;
    }

    // Calculate how much of the current stage is completed (as a percentage)
    const progressInCurrentStage = Math.max(0, Math.min(progress - currentPosition, positionDifference));
    const percentComplete = progressInCurrentStage / positionDifference;

    // Calculate days based on position in current stage
    const daysPerPercent = maxDaysToHarvest / 100;
    const totalDaysInStage = positionDifference * daysPerPercent;
    const daysRemaining = Math.round(totalDaysInStage * (1 - percentComplete));

    return Math.max(0, daysRemaining);
  };

  // Load plants database using the shared service
  useEffect(() => {
    const loadPlantDatabase = async () => {
      setIsLoading(true);
      try {
        const plants = await plantDataService.getPlantData();
        setPlantDatabase(plants);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading plant database:", error);
        setError("Failed to load plants. Please refresh the page.");
        setIsLoading(false);

        // Fallback to mock data for testing
        const mockData = [
          {
            plant_id: 1,
            common_name: "Tomato",
            scientific_name: "Solanum lycopersicum",
            category: "Vegetable",
            min_days_to_harvest: 60,
            max_days_to_harvest: 100,
            optimal_season: "Warm season",
            optimal_season_notes: "spring-summer",
            planting_condition: "Transplant after last frost; provide stakes or cage",
            min_temperature_c: 21,
            max_temperature_c: 24,
            rainfall_level: "Moderate",
            rainfall_notes: "~1/week",
            partial_shade_adaptable: "No",
            watering_frequency_level: "Regular",
            watering_notes: "keep soil evenly moist",
            fertilizer_required: "Yes",
            fertilizer_notes: "moderate, avoid excess nitrogen",
            growth_difficulty: "Moderate",
            common_pests: "Aphids, hornworms, whiteflies",
            special_notes: "Frost-sensitive; prone to blight and blossom-end rot",
            plant_pictures: null,
            representative_image_url: "https://yourgardenguru-repre.s3.ap-southeast-2.amazonaws.com/tomato.png"
          }
        ];

        setPlantDatabase(mockData);
      }
    };

    loadPlantDatabase();

    // Subscribe to plant data changes
    const unsubscribe = plantDataService.subscribe((updatedPlants) => {
      setPlantDatabase(updatedPlants);
    });

    return unsubscribe;
  }, []);

  // Search plants in database when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const filteredPlants = plantDatabase.filter(plant =>
      plant.common_name.toLowerCase().includes(searchTerm.toLowerCase())
      // plant.scientific_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSearchResults(filteredPlants);
    setShowDropdown(filteredPlants.length > 0);
  }, [searchTerm, plantDatabase]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch plant stages when selected plant changes
  useEffect(() => {
    if (selectedPlant && selectedPlant.plantData) {
      // Use the common_name from the plantData to fetch stages
      fetchPlantStages(selectedPlant.plantData.common_name);

      // If plantStages is already loaded but plant stage isn't set, initialize it
      if (plantStages.length > 0 && (!selectedPlant.stage || selectedPlant.stage === '')) {
        const updatedPlant = {
          ...selectedPlant,
          stage: plantStages[0].stage_name
        };
        gardenService.updatePlant(updatedPlant);
        ecoReportCacheService.invalidateCache();

        setSelectedPlant(updatedPlant);
      }
    } else {
      // Clear stages if no plant selected
      setPlantStages([]);
    }
  }, [selectedPlant]);

  // Handle dragging plants
  const handleMouseDown = (e, plant) => {
    e.preventDefault();

    const rect = gardenRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - plant.x;
    const offsetY = e.clientY - rect.top - plant.y;

    setDraggedPlant({ ...plant, offsetX, offsetY });
    setSelectedPlant(plant);
    setShowPlantMenu(false);
  };

  // Handle mouse movement while dragging a plant
  const handleMouseMove = (e) => {
    if (!draggedPlant) return;

    const rect = gardenRef.current.getBoundingClientRect();
    const x = Math.max(20, Math.min(e.clientX - rect.left - draggedPlant.offsetX, rect.width - 20));
    const y = Math.max(20, Math.min(e.clientY - rect.top - draggedPlant.offsetY, rect.height - 20));

    setPlants(plants.map(plant =>
      plant.id === draggedPlant.id ? { ...plant, x, y } : plant
    ));

    // Create an updated plant
    const updatedPlant = { ...draggedPlant, x, y };
    delete updatedPlant.offsetX;
    delete updatedPlant.offsetY;

    // Use service to update plants
    gardenService.updatePlant(updatedPlant);
  };

  // Handle mouse up when dropping a plant
  const handleMouseUp = (e) => {
    if (!draggedPlant) return;

    // Check if plant is dropped in bin
    if (binRef.current) {
      const binRect = binRef.current.getBoundingClientRect();
      const plantX = e.clientX;
      const plantY = e.clientY;

      if (
        plantX >= binRect.left &&
        plantX <= binRect.right &&
        plantY >= binRect.top &&
        plantY <= binRect.bottom
      ) {
        // Remove the plant using service
        setPlantToDelete(draggedPlant);
        setShowDeleteConfirm(true);
      }
    }

    setDraggedPlant(null);
  };

  // Function to handle confirmation for deleting a plant
  const handleConfirmDelete = () => {
    if (plantToDelete) {
      // Remove the plant using service
      gardenService.removePlant(plantToDelete.id);

      ecoReportCacheService.invalidateCache();

      // If the deleted plant was selected, clear selection
      if (selectedPlant && selectedPlant.id === plantToDelete.id) {
        setSelectedPlant(null);
      }

      // Reset states
      setPlantToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  // Function to cancel deletion of plant
  const handleCancelDelete = () => {
    setPlantToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Handle clicking a plant to show details popup
  const handlePlantClick = (e, plant) => {
    if (draggedPlant) return;

    e.stopPropagation();
    setSelectedPlant(plant);

    const rect = gardenRef.current.getBoundingClientRect();
    setMenuPosition({
      x: plant.x + rect.left,
      y: plant.y + rect.top - 60
    });

    setShowPlantMenu(true);
  };

  // Handle clicking garden background to close menus
  const handleGardenClick = () => {
    setShowPlantMenu(false);
  };

  // Handle selecting a plant from dropdown
  const handleSelectPlant = (plant) => {
    setSelectedPlantType(plant);
    setSearchTerm(plant.common_name);
    setShowDropdown(false);

    addSelectedPlant(plant)

    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }, 50);
  };

  // Modified function to add plants that works with the tour
  // Add the selected plant to the garden
  const addSelectedPlant = (plantData) => {
    // console.log('selectedPlantType', selectedPlantType)
    // console.log('searchTerm', searchTerm)
    // console.log('common_name', plantData.common_name)
    let matchingPlant
    if (!selectedPlantType && plantData.common_name) {
      // Try to find a matching plant in the database
      // console.log('searching the db for', plantData.common_name)
      matchingPlant = plantDatabase.find(plant =>
        plant.common_name.toLowerCase() === plantData.common_name.toLowerCase()
      );

      if (matchingPlant) {
        // console.log('found it an setting', matchingPlant)
        setSelectedPlantType(matchingPlant);
        // console.log('setSelectedPlantType', selectedPlantType)

      } else {
        alert("Please select a valid plant from the dropdown list");
        return;
      }
    }

    if (!selectedPlantType && searchTerm.trim() === '') {
      alert("Please search and select a plant first");
      return;
    }

    const plant = selectedPlantType || matchingPlant || plantDatabase[0];
    // console.log('plant', plant)
    // console.log('matchingPlant', matchingPlant)


    const gardenWidth = gardenRef.current.clientWidth - 50;
    const gardenHeight = gardenRef.current.clientHeight - 50;

    // Get current date for the new plant
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    // Calculate expected date based on max_days_to_harvest
    const expectedDate = new Date();
    const daysToHarvest = plant.max_days_to_harvest || 90; // Default to 90 days if not specified
    expectedDate.setDate(today.getDate() + daysToHarvest);
    const formattedExpectedDate = expectedDate.toISOString().split('T')[0];

    // Create plant type if it doesn't exist in plantTypes
    const plantType = plant.common_name.toLowerCase();
    if (!plantTypes[plantType]) {
      console.log(`Added new plant type: ${plantType}`);
      gardenService.addPlantType(plantType, {
        image: plant.representative_image_url || plant.icon_image_url || placeholderImg
      });
    }

    // Calculate position based on tour or random
    let xPos, yPos;
    if (tourActive && tourStep <= 2) {
      // Place in center of visible garden area
      xPos = Math.floor(gardenWidth / 2) + 25;
      yPos = Math.floor(gardenHeight / 2) + 25;
    } else {
      // Random position
      xPos = Math.floor(Math.random() * gardenWidth) + 25;
      yPos = Math.floor(Math.random() * gardenHeight) + 25;
    }

    // Create the new plant
    const newPlant = {
      id: Date.now(),
      type: plantType,
      x: xPos,
      y: yPos,
      status: 'healthy',
      progress: 0,
      nextTask: 'water',
      waterLevel: 100,
      health: 100,
      startDate: formattedDate,
      stage: '', // We'll update this after fetching stages
      expectedDate: formattedExpectedDate,
      // Store all plant data from API
      plantData: plant,
      imageUrl: plant.representative_image_url,
      icon_image_url: plant.icon_image_url,
      tourPlant: tourActive // Mark if this is a tour plant
    };

    // Use service to add plant
    gardenService.addPlant(newPlant);

    ecoReportCacheService.invalidateCache();

    // If we're on the tour, update relevant states
    if (tourActive && tourStep <= 1) {
      // Update the tourPlantAdded state to true
      setTourPlantAdded(true);
      setSelectedPlant(newPlant);

      // Enable the next button (in case it was disabled)
      const nextButton = document.querySelector('.introjs-nextbutton');
      if (nextButton) {
        nextButton.classList.remove('introjs-disabled');
        nextButton.style.pointerEvents = 'auto';
        nextButton.style.opacity = '1';

        // Remove the hint if it exists
        const hint = document.querySelector('.next-button-hint');
        if (hint) {
          hint.remove();
        }

        setTimeout(() => {
          nextButton.click();
        }, 100);
      }

      // Add a success message to encourage clicking Next
      const successMessage = document.createElement('div');
      successMessage.className = 'plant-added-success';
      successMessage.textContent = '✅ Plant added! Click Next to continue';
      successMessage.style.color = '#4caf50';
      successMessage.style.fontSize = '12px';
      successMessage.style.textAlign = 'center';
      successMessage.style.marginTop = '8px';
      successMessage.style.fontWeight = 'bold';

      const buttonsContainer = document.querySelector('.introjs-tooltipbuttons');
      if (buttonsContainer && !document.querySelector('.plant-added-success')) {
        buttonsContainer.appendChild(successMessage);

        // Remove the success message after 3 seconds
        setTimeout(() => {
          if (successMessage.parentNode) {
            successMessage.parentNode.removeChild(successMessage);
          }
        }, 3000);
      }
    } else {
      setSelectedPlant(newPlant);
    }

    // Fetch stages for this plant immediately after adding
    fetchPlantStages(plant.common_name);

    // Add pixel-style popup effect
    const popupEl = document.createElement('div');
    popupEl.className = 'pixel-popup';
    popupEl.textContent = `${plant.common_name} Added!`;
    popupEl.style.left = `${newPlant.x}px`;
    popupEl.style.top = `${newPlant.y - 40}px`;
    gardenRef.current.appendChild(popupEl);

    setTimeout(() => {
      if (gardenRef.current && gardenRef.current.contains(popupEl)) {
        gardenRef.current.removeChild(popupEl);
      }
    }, 1000);

    // Reset after adding
    setSelectedPlantType(null);
    setSearchTerm('');

    setTimeout(() => {
      if (tourRef.current) {
        tourRef.current.refresh();    // re-index dynamically added DOM elements
        tourRef.current.nextStep();  // advance from step 5 into 6
      }
    }, 5000);
  };

  const viewPlantSearch = () => {
    console.log("switch")
    // addSelectedPlant
  }

  // NEW FUNCTION: Add a demo plant for the tour
  const addDemoPlant = () => {

    console.log("Attempting to add demo plant...", {
      plantsLength: plants.length,
      databaseLength: plantDatabase.length,
      demoAdded: demoPlantAdded,
      gardenRef: Boolean(gardenRef.current)
    });

    if (plants.length > 0 || demoPlantAdded) {
      return false;
    }

    // Only add if we don't already have plants and database is loaded
    if (plantDatabase.length > 0 && gardenRef.current) {
      // Use the first plant from the database
      const demoPlantType = plantDatabase[0];

      // Calculate position in center of garden
      const gardenWidth = gardenRef.current?.clientWidth || 500;
      const gardenHeight = gardenRef.current?.clientHeight || 500;
      const xPos = Math.floor(gardenWidth / 2);
      const yPos = Math.floor(gardenHeight / 2);

      // Get current date
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];

      // Calculate expected date based on max_days_to_harvest
      const expectedDate = new Date();
      const daysToHarvest = demoPlantType.max_days_to_harvest || 90;
      expectedDate.setDate(today.getDate() + daysToHarvest);
      const formattedExpectedDate = expectedDate.toISOString().split('T')[0];

      // Create the demo plant
      const demoPlant = {
        id: Date.now(),
        type: demoPlantType.common_name.toLowerCase(),
        x: xPos,
        y: yPos,
        status: 'healthy',
        progress: 10, // Start with some progress
        nextTask: 'water',
        waterLevel: 100,
        health: 100,
        startDate: formattedDate,
        stage: 'seedling', // Set initial stage
        expectedDate: formattedExpectedDate,
        plantData: demoPlantType,
        imageUrl: demoPlantType.representative_image_url,
        icon_image_url: demoPlantType.icon_image_url,
        isDemoPlant: true // Mark as demo plant
      };

      // Use service to add plant
      gardenService.addPlant(demoPlant);

      // Set demo plant as selected plant
      setSelectedPlant(demoPlant);

      // Update state
      setDemoPlantAdded(true);

      // Fetch stages for the demo plant
      fetchPlantStages(demoPlantType.common_name);

      console.log('Added demo plant for tour:', demoPlant);
      return true;
    }
    return false;
  };

  // Add this function to prepare steps conditionally based on state
  const getGardenTourSteps = () => {
    const steps = [
      { element: '.pixel-heading', intro: 'Virtual Garden is where you can manage your plants digitally.', position: 'bottom' },
      { element: '.pixel-garden-controls', intro: "Add a plant into your virtual garden.", position: 'left' },
      { element: '.pixel-garden-plot', intro: 'This is your garden area where you can see all your plants. Click on a plant to see its details.', position: 'right' },
      { element: '.pixel-plant-details', intro: 'This sidebar shows detailed information about your selected plant.', position: 'left' },
      { element: '.pixel-growth-section', intro: 'This section shows the growth stages and days remaining until the next stage.', position: 'bottom', highlightClass: 'growth-section-highlight' },
      { element: '.pixel-current-stage-info', intro: 'This shows the current stage of your plant. You can also modify it to match your actual plant situation.', position: 'left' },
      { element: '.pixel-plant-task-container', intro: 'Set reminders for your plants here.', position: 'top' },
      { element: '.pixel-garden-bin', intro: 'To remove a plant, drag and drop it into this bin.', position: 'top' }
    ];

    return steps;
  };


  // Define a function to start the tour with disabled next button functionality
  const startGardenTour = () => {

    setTourActive(true);
    setTourStep(1);

    if (plants.length === 0 && !demoPlantAdded) {
      const plantAdded = addDemoPlant();

      if (!plantAdded) {
        console.log("Failed to add demo plant on first try, waiting and trying again");
        setTimeout(() => {
          addDemoPlant();
        }, 500);
      }
    }

    // Configure the tour
    const tour = introJs();
    tourRef.current = tour;

    const steps = getGardenTourSteps();

    // Set options
    tour.setOptions({
      steps: steps,
      showStepNumbers: false,
      showBullets: true,
      exitOnOverlayClick: false,
      exitOnEsc: false,
      overlayOpacity: 0.7,
      scrollToElement: true,
      doneLabel: 'Finish',
      nextLabel: 'Next →',
      prevLabel: '← Back',
      disableInteraction: false,
      // helperElementPadding: 10,
      hintPosition: 'top-middle',
      tooltipClass: 'garden-tour-tooltip',
      skipLabel: ''
      // exitOnOverlayClick:false
    });


    // Add event listeners for the tour
    tour.onbeforechange(function (targetElement) {
      const step = tour._currentStep;
      setTourStep(step);

      if ((step === 3 || step >= 4) && plants.length > 0 && !selectedPlant) {
        // Find the demo plant
        const demoPlant = plants.find(p => p.isDemoPlant) || plants[0];
        setSelectedPlant(demoPlant);
      }

      // Handle special cases
      if (step === 3) {
        setTimeout(() => {
          const plantElements = document.querySelectorAll('.pixel-plant-item');
          if (plantElements.length > 0) {
            // Click on the demo plant
            const demoPlantElement = Array.from(plantElements).find(el => {
              const plantId = el.getAttribute('data-id');
              const plant = plants.find(p => p.id.toString() === plantId);
              return plant && plant.isDemoPlant;
            }) || plantElements[0];

            if (demoPlantElement) {
              demoPlantElement.click();
            }
          }
          tour.refresh();
        }, 200);
      } else if (step === 4 || step === 5 || step === 6) {
        // For sidebar detail steps, make sure sidebar is visible and refresh positions
        setTimeout(() => {
          tour.refresh();
        }, 100);
      }

      return true;
    });

    tour.onchange(function (targetElement) {
      setTourStep(tour._currentStep);
    });

    // Add a handler for after step change - useful for animations or special highlighting
    // tour.onafterchange(function(targetElement) {

    //   if (tour._currentStep === 0) {
    //     setTimeout(() => {
    //       const nextButton = document.querySelector('.introjs-nextbutton');
    //       if (nextButton) {
    //         nextButton.classList.remove('introjs-disabled');
    //         nextButton.style.pointerEvents = 'auto';
    //         nextButton.style.opacity = '1';
    //       }
    //     }, 100);
    //   }
    //   // If we're on step 1 (search/add plant)
    //   else if (tour._currentStep === 1) {
    //     // Check if a plant has been added
    //     if (!tourPlantAdded && tour._direction === 'forward') {
    //       // Ensure button is disabled (sometimes the DOM gets refreshed)
    //       setTimeout(() => {
    //         setNextButtonState(true);
    //       }, 100);
    //     } else {
    //       if (tour._direction === 'backward') {
    //         setTimeout(() => {
    //           setNextButtonState(false);
    //         }, 100);
    //       }
    //     }
    //   }
    // });

    tour.oncomplete(function () {
      console.log("Tour completed, cleaning up...");
      setTourActive(false);

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 60);

      document.cookie = `gardenTourComplete=true; expires=${expiryDate.toUTCString()}; path=/;`;

      requestAnimationFrame(() => {
        console.log("Running cleanup after tour completion");
        forceRemoveDemoPlant();
      });

    });

    tour.onexit(function () {
      console.log("Tour exited, cleaning up...");
      setTourActive(false);

      requestAnimationFrame(() => {
        console.log("Running cleanup after tour completion");
        forceRemoveDemoPlant();
      });
    });

    // Start the tour with dontShowAgain option
    tour.setOption("dontShowAgain", true)
      .setOption("dontShowAgainLabel", "Do not show it again.")
      .setOption("dontShowAgainCookie", "gardenTourComplete") // Custom cookie name
      .setOption("dontShowAgainCookieDays", 60) // Cookie expires after 1 year
      .start();
  };

  // Add this new function for direct removal that doesn't depend on current state
  const forceRemoveDemoPlant = () => {
    // Get plants directly from service to ensure we have the latest data
    const currentPlants = gardenService.getPlants();
    const demoPlant = currentPlants.find(plant => plant.isDemoPlant === true);

    if (demoPlant) {
      console.log("Force removing demo plant:", demoPlant.id);

      // Create a local variable to check if we need to clear selected plant
      // const isSelected = selectedPlant && selectedPlant.id === demoPlant.id;

      // Remove the plant using service
      gardenService.removePlant(demoPlant.id);

      // Update states after removal
      setPlants(gardenService.getPlants());
      setDemoPlantAdded(false);

      // clear sidebar for demo plants
      setSelectedPlant(null);

      console.log('Demo plant force removal completed');
      return true;
    } else {
      console.log("No demo plant found to remove");
      return false;
    }
  };

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (selectedStage) {
          setSelectedStage(null);
        }
        if (isEditingHealth) {
          setIsEditingHealth(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [selectedStage, isEditingHealth]);

  // Attach and remove event listeners for drag functionality
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggedPlant, plants]);

  // Subscribe to plant changes
  useEffect(() => {
    const unsubscribe = gardenService.subscribe(updatedPlants => {
      setPlants(updatedPlants);
    });

    return unsubscribe;
  }, []);

  // useEffect(() => {
  //   // Skip if already initialized or if resources aren't loaded yet
  //   if (tourRef.current || isLoading || plantDatabase.length === 0) return;

  //   const timer = setTimeout(() => {
  //     const shouldShowTour = !document.cookie.includes('gardenTourComplete=true');

  //     if (shouldShowTour) {
  //       if (plants.length === 0 && !demoPlantAdded) {
  //         // Add demo plant first
  //         addDemoPlant();

  //         // Wait for plant to be added to DOM and state
  //         setTimeout(() => {
  //           // Only start tour after plant is added
  //           const tour = introJs();
  //           tourRef.current = tour;

  //           tour.setOption("dontShowAgain", true)
  //             .setOption("dontShowAgainCookie", "gardenTourComplete");

  //           if (tour.isActive()) {
  //             startGardenTour();
  //           }
  //         }, 500); // Wait 500ms for plant to render
  //       } else {
  //         // We already have plants, just check if tour should start
  //         const tour = introJs();
  //         tourRef.current = tour;

  //         tour.setOption("dontShowAgain", true)
  //           .setOption("dontShowAgainCookie", "gardenTourComplete");

  //         if (tour.isActive()) {
  //           startGardenTour();
  //         }
  //       }
  //     }
  //   }, 1000);

  //   return () => clearTimeout(timer);
  // }, [isLoading, plantDatabase, plants, demoPlantAdded]);



  // Modified function to handle tour step transitions
  // useEffect(() => {
  //   if (tourActive) {
  //     // When reaching step 3 (before sidebar details steps)
  //     if (tourStep === 3) {
  //       // Ensure a plant is selected
  //       const plants = document.querySelectorAll('.pixel-plant-item');
  //       if (plants.length > 0) {
  //         // Select the first plant to ensure sidebar content is visible
  //         const firstPlant = plants[0];
  //         const plantId = firstPlant.getAttribute('data-id');

  //         // Find the plant object by ID
  //         const plantObject = gardenService.getPlants().find(p => p.id.toString() === plantId);

  //         if (plantObject) {
  //           // Set as selected plant to show sidebar details
  //           setSelectedPlant(plantObject);

  //           // Simulate click to ensure any click handlers run
  //           firstPlant.click();

  //           // Tell intro.js to refresh element positions
  //           if (tourRef.current) {
  //             setTimeout(() => {
  //               tourRef.current.refresh();
  //             }, 100);
  //           }
  //         }
  //       }
  //     }
  //   }
  // }, [tourStep, tourActive]);

  useEffect(() => {
    // When selected task changes, automatically update the recommended settings
    if (selectedPlant && selectedTask) {
      // Set recommended days based on plant type and task
      const recommendedDays = getSuggestedDays();
      setDaysFromNow(recommendedDays);

      // Set recommended time (9 AM for morning tasks, 6 PM for evening)
      if (['water', 'fertilise'].includes(selectedTask)) {
        setReminderHour(9); // Morning is better for watering and fertilizing
      } else {
        setReminderHour(18); // Evening might be better for other tasks
      }

      setReminderMinute(0);
    }
  }, [selectedTask, selectedPlant]);

  // Add this useEffect to handle window resizing
  useEffect(() => {
    // Function to update garden dimensions
    const updateGardenDimensions = () => {
      if (gardenRef.current) {
        const newWidth = gardenRef.current.clientWidth;
        const newHeight = gardenRef.current.clientHeight;

        // Only update if dimensions have actually changed
        if (newWidth !== gardenDimensions.width || newHeight !== gardenDimensions.height) {
          // Store previous dimensions before updating
          setPreviousDimensions(gardenDimensions);
          setGardenDimensions({ width: newWidth, height: newHeight });
        }
      }
    };

    // Initial size calculation
    updateGardenDimensions();

    // Add resize event listener
    window.addEventListener('resize', updateGardenDimensions);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', updateGardenDimensions);
    };
  }, [gardenDimensions]);

  // Add this useEffect to reposition plants when garden dimensions change
  useEffect(() => {
    // Skip initial render or if no previous dimensions are stored
    if (previousDimensions.width === 0 || previousDimensions.height === 0) {
      return;
    }

    // Skip if dimensions haven't changed significantly (to avoid unnecessary updates)
    if (
      Math.abs(gardenDimensions.width - previousDimensions.width) < 10 &&
      Math.abs(gardenDimensions.height - previousDimensions.height) < 10
    ) {
      return;
    }

    // Calculate scaling factors
    const widthRatio = gardenDimensions.width / previousDimensions.width;
    const heightRatio = gardenDimensions.height / previousDimensions.height;

    // Update the position of all plants proportionally
    const updatedPlants = plants.map(plant => {
      // Calculate new positions based on scaling ratios
      const newX = Math.min(
        Math.max(25, plant.x * widthRatio), // Ensure plants stay within bounds with minimum 25px from edge
        gardenDimensions.width - 25
      );

      const newY = Math.min(
        Math.max(25, plant.y * heightRatio), // Ensure plants stay within bounds with minimum 25px from edge
        gardenDimensions.height - 25
      );

      // Create updated plant with new coordinates
      const updatedPlant = { ...plant, x: newX, y: newY };

      // Update plant in service
      gardenService.updatePlant(updatedPlant);

      return updatedPlant;
    });

    // Update the plants state
    setPlants(updatedPlants);

    // If a plant is selected, update its position in the selection
    if (selectedPlant) {
      const updatedSelectedPlant = updatedPlants.find(p => p.id === selectedPlant.id);
      if (updatedSelectedPlant) {
        setSelectedPlant(updatedSelectedPlant);
      }
    }
  }, [gardenDimensions, previousDimensions]);

  return (
    <div className="pixel-garden">
      <div className="pixel-garden-header">
        <h2 className="pixel-heading" data-intro="Virtual Garden is where you can track and manage your plants digitally." data-step="1">My Virtual Garden</h2>

        <div
          className="pixel-garden-controls"
          data-intro="Try Search 'Tomato' and click the Add Plant button."
          data-step="2"
          data-position="left"
        >
          <div
            className="pixel-search-container"
            ref={searchInputRef}
          >
            <input
              type="text"
              placeholder="Add a new plant..."
              className="pixel-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowDropdown(true);
                }
              }}
              disabled={isLoading}
            />

            {/* <span class="placeholder">+ <span class="small">Add New Plant</span></span> */}

            {/* Loading indicator */}
            {isLoading && (
              <div className="pixel-loading">Loading plants...</div>
            )}

            {/* Dropdown for search results */}
            {showDropdown && !isLoading && (
              <ul className="pixel-search-dropdown">
                {searchResults.map((plant) => (
                  <li
                    key={plant.plant_id}
                    className="pixel-dropdown-item"
                    onClick={() => {
                      handleSelectPlant(plant)
                      // addSelectedPlant()
                    }
                    }
                  >
                    <div className="pixel-dropdown-content">
                      {plant.representative_image_url && (
                        <img
                          src={plant.representative_image_url}
                          alt={plant.common_name}
                          className="pixel-dropdown-image"
                        />
                      )}
                      <div className="pixel-dropdown-text">
                        <div className="pixel-dropdown-name">{plant.common_name}</div>
                        <div className="pixel-dropdown-scientific">{plant.scientific_name}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
      
        </div>
      </div>

      <div className="pixel-garden-content">
        {/* Garden visualization area */}
        <div
          ref={gardenRef}
          className="pixel-garden-plot"
          onClick={handleGardenClick}
          data-intro="This is your garden area where you can see all your plants. Click on a plant to see its details."
          data-step="3"
          data-position="right"
        >
          {/* Garden grid for that pixel game look */}
          <div className="pixel-garden-grid">
            {/* Grid is created with CSS */}
          </div>

          {/* Plants */}
          {plants.map(plant => (
            <div
              key={plant.id}
              data-id={plant.id}
              className={`pixel-plant-item ${selectedPlant?.id === plant.id ? 'selected' : ''}`}
              style={{
                left: `${plant.x}px`,
                top: `${plant.y}px`,
                cursor: draggedPlant?.id === plant.id ? 'grabbing' : 'grab'
              }}
              onMouseDown={(e) => handleMouseDown(e, plant)}
              onClick={(e) => handlePlantClick(e, plant)}
            >
              <img
                src={plant.icon_image_url || plant.imageUrl || (plantTypes[plant.type]?.image || placeholderImg)}
                alt={plant.type}
                className="pixel-plant-image"
              />
              <span className="pixel-plant-label" style={{ textTransform: 'capitalize' }}>{plant.type}</span>
              <div
                className={`pixel-plant-health-dot pixel-health-${plant.status}`}
                title={`Health: ${plant.status}`}
              ></div>


              {/* Popup menu when plant is clicked */}
              {showPlantMenu && selectedPlant?.id === plant.id && (
                <div className="pixel-plant-popup">
                  {/* <h4 className="pixel-popup-heading">{plant.type}</h4> */}
                  <p className="pixel-popup-text">
                    <b>Status:</b>
                    <span
                      className={`pixel-health-indicator pixel-health-${plant.status}`}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {plant.status}
                    </span>
                  </p>
                  <p className="pixel-popup-text">
                    <b>Next:</b> <span style={{ textTransform: 'capitalize' }}>{plant.nextTask}</span>
                  </p>
                  <div className="pixel-popup-progress">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`pixel-progress-unit ${i < plant.progress / 10 ? 'filled' : ''}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Recycle Bin */}
          <div
            ref={binRef}
            className="pixel-garden-bin"
            data-intro="To remove a plant, drag and drop it into this bin. Try dragging your plant here to delete it."
            data-step="8"
          >
            <img src={binImg} alt="Bin" className="pixel-bin-image" />
          </div>
        </div>

        {/* Plant details sidebar */}
        <div
          className="pixel-plant-details"
          data-intro="This sidebar shows detailed information about your selected plant."
          data-step="5"
        >
          {selectedPlant ? (
            <>
              <div
                className="pixel-plant-header"
                data-intro="This shows the name of your plant."
                data-step="6"
              >
                <div className="plant-name-and-type">
                  <p>{selectedPlant.plantData.common_name}</p>
                  {selectedPlant.plantData?.category && (
                    <div className="pixel-plant-category">
                      <span>{selectedPlant.plantData.category}</span>
                    </div>
                  )}
                </div>

                {selectedPlant.plantData?.scientific_name && (
                  <div
                    className="pixel-plant-info-item-scientific"
                    data-intro="Here you can see the scientific name of your plant."
                    data-step="7"
                  >
                    {selectedPlant.plantData.scientific_name}
                  </div>
                )}

              </div>

              <div
                className="pixel-plant-image-container"
                onClick={selectedPlant.imageUrl ? () => setEnlargedImage(selectedPlant.imageUrl) : undefined}
              >
                {selectedPlant.imageUrl ? (
                  <img
                    src={selectedPlant.imageUrl}
                    alt={selectedPlant.type}
                    className="pixel-plant-detail-image"
                  />
                ) : (
                  <div className="pixel-plant-image-placeholder">
                    +
                  </div>
                )}
              </div>

              <div className="pixel-garden-plant-info">
                <div className="plant-start-and-info-box">
                  <div className="pixel-plant-info-item">
                    <span className="pixel-plant-info-label"><b>Plant Started: </b>
                      {selectedPlant.startDate}</span>
                  </div>
                  {/* Allow user to modify the health status */}
                  <div className="pixel-plant-info-item">
                    <span className="pixel-plant-info-label"><b>Plant Health: </b></span>
                    {!isEditingHealth ? (
                      <>
                        <span className={`pixel-health-indicator pixel-health-${selectedPlant.status}`} style={{ textTransform: 'capitalize' }}>
                          {selectedPlant.status}
                        </span>
                        <button
                          className="pixel-edit-stage-btn"
                          onClick={() => setIsEditingHealth(true)}
                          title="Update health status"
                        >
                          ✏️
                        </button>
                      </>
                    ) : (
                      <>
                        <select
                          value={selectedPlant.status}
                          onChange={(e) => {
                            const updatedPlant = {
                              ...selectedPlant,
                              status: e.target.value
                            };
                            gardenService.updatePlant(updatedPlant);
                            ecoReportCacheService.invalidateCache();
                            setSelectedPlant(updatedPlant);
                            setIsEditingHealth(false);
                          }}
                          className="pixel-health-select"
                          autoFocus
                          onBlur={() => setIsEditingHealth(false)}
                        >
                          {PLANT_HEALTH_STATUSES.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>

                  {/* Growth Journey Information */}
                  <div className="pixel-plant-info-item">
                    <div
                      className="pixel-growth-section"
                      data-intro="This section shows the growth progress of your plant and the days remaining until the next growth stage."
                      data-step="7"
                    >
                      {/* Loading indicator for stages */}
                      {isLoadingStages && (
                        <div className="pixel-loading-stages">Loading growth stages...</div>
                      )}

                      {/* Prominently display days to next stage if available */}
                      {selectedPlant && !isLoadingStages && (() => {
                        // Use the current stage from the plant, if not available use first stage from API data
                        const plantStage = selectedPlant.stage || (plantStages.length > 0 ? plantStages[0].stage_name : '');

                        // Only proceed if we have a valid stage
                        if (plantStage && plantStage !== '') {
                          const { nextStage } = getCurrentStageInfo(plantStage);

                          try {
                            if (nextStage) {
                              return (
                                <div className="pixel-next-stage-countdown">
                                  <div className="pixel-countdown-value">
                                    {calculateDaysToNextStage(
                                      selectedPlant.startDate,
                                      plantStage,
                                      selectedPlant.plantData?.max_days_to_harvest || 100
                                    )}
                                  </div>
                                  <div className="pixel-countdown-label">
                                    days until <span className="pixel-stage-name">{nextStage.name}</span>
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="pixel-ready-to-harvest">
                                  Ready to Harvest!
                                </div>
                              );
                            }
                          } catch (error) {
                            console.error("Error in stage calculation:", error);
                            return (
                              <div className="pixel-next-stage-countdown">
                                <div className="pixel-countdown-value">--</div>
                                <div className="pixel-countdown-label">days until next stage</div>
                              </div>
                            );
                          }
                        } else {
                          return (
                            <div className="pixel-loading-stages">Loading growth stages...</div>
                          );
                        }
                      })()}

                      {/* Current stage shown as smaller text */}
                      <div className="pixel-current-stage-info">
                        Current stage:
                        <span>&nbsp;</span>
                        {!isEditingStage ? (
                          <>
                            <span className="pixel-current-stage-name">
                              {selectedPlant.stage || (plantStages.length > 0 ? plantStages[0].stage_name : 'Loading...')}
                            </span>
                            <button
                              className="pixel-edit-stage-btn"
                              onClick={() => setIsEditingStage(true)}
                              title="Update stage to match your real garden"
                            >
                              ✏️
                            </button>
                          </>
                        ) : (
                          <>
                            <select
                              value={selectedPlant.stage}
                              onChange={(e) => handleStageChange(e.target.value)}
                              className="pixel-stage-select"
                              autoFocus
                              onBlur={() => setIsEditingStage(false)}
                            >
                              {plantStages.length > 0 ? (
                                plantStages.map(stage => (
                                  <option key={stage.stage_name} value={stage.stage_name}>
                                    {stage.stage_name}
                                  </option>
                                ))
                              ) : (
                                getDefaultGrowthStages().map(stage => (
                                  <option key={stage.name} value={stage.name}>
                                    {stage.name}
                                  </option>
                                ))
                              )}
                            </select>
                            <div className="pixel-stage-hint">
                              Select the current growth stage of your real plant
                            </div>
                          </>
                        )}
                      </div>

                      {/* Progress Bar Container */}
                      <div className="pixel-progress-container">
                        <div className="pixel-progress-bar-container">
                          <div
                            className="pixel-progress-bar"
                            style={{
                              width: `${selectedPlant.progress !== undefined ?
                                selectedPlant.progress :
                                (selectedPlant.plantData?.max_days_to_harvest ?
                                  calculateTimeProgress(selectedPlant.startDate, selectedPlant.plantData.max_days_to_harvest) : 0)}%`
                            }}
                          >
                            {/* Removed percentage from inside the bar */}
                          </div>

                          {/* Move percentage below the progress bar */}
                          <div className="pixel-progress-percentage">
                            <span className="pixel-progress-value">
                              {selectedPlant.progress !== undefined ?
                                selectedPlant.progress :
                                (selectedPlant.plantData?.max_days_to_harvest ?
                                  calculateTimeProgress(selectedPlant.startDate, selectedPlant.plantData.max_days_to_harvest) : 0)}%
                            </span>
                          </div>

                          {/* Current day marker */}
                          <div
                            className="pixel-current-day-marker"
                            style={{
                              left: `${selectedPlant.progress !== undefined ?
                                selectedPlant.progress :
                                (selectedPlant.plantData?.max_days_to_harvest ?
                                  calculateTimeProgress(selectedPlant.startDate, selectedPlant.plantData.max_days_to_harvest) : 0)}%`
                            }}
                            title={`Day ${calculateDaysSincePlanting(selectedPlant.startDate)}`}
                          />

                          {/* Harvest point at the end */}
                          <div className="pixel-harvest-days-label">
                            {selectedPlant.plantData?.max_days_to_harvest || 100} days
                          </div>

                          {/* Stage markers */}
                          {getGrowthStages().map((stage, index) => {
                            // Make sure selectedPlant.stage exists, otherwise default to first stage
                            const plantStage = selectedPlant.stage || (getGrowthStages()[0]?.name || 'seedling');
                            const { currentStageIndex } = getCurrentStageInfo(plantStage);

                            return (
                              <div
                                key={index}
                                className={`pixel-stage-marker ${index < currentStageIndex ? 'pixel-passed' :
                                  index === currentStageIndex ? 'pixel-current' : ''
                                  }`}
                                style={{ left: `${stage.position}%` }}
                                onClick={() => setSelectedStage(stage)}
                                title={stage.name}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='pixel-plant-task-container'>
                  <div className="pixel-plant-task-row">
                    <div>
                      <span className="pixel-plant-info-label">Next Task: </span>
                      <b><span style={{ textTransform: 'capitalize' }}>{selectedPlant.nextTask}</span></b>
                    </div>
                    <button
                      className={`pixel-reminder-button ${hasReminder ? 'active' : ''}`}
                      onClick={() => setShowReminderCalendar(true)}
                    >
                      {hasReminder ? '✓ Reminder Set' : '+ Set Reminder'}
                    </button>
                  </div>
                </div>

              </div>
            </>
          ) : (
            <div className="pixel-no-selection">
              <img
                src={placeholderImg}
                alt="Garden"
                className="pixel-garden-icon"
              />
              <p className="pixel-instruction-text">* Select a plant to see its details.</p>
              <p className="pixel-instruction-text">* Drag plants to rearrange.</p>
              <p className="pixel-instruction-text">* Drop a plant in the bin to remove plant.</p>

            </div>
          )}
        </div>
      </div>

      {/* <div className="pixel-dashboard-cards">
        {/* <div className="pixel-card"> */}
        {/* <GardenEcoSummary /> */}
        {/* </div> */}

        {/* <div className="pixel-card">
          <PlantsSummary />
        </div>
      </div> */} 

      {/* Calendar Popup for Reminder */}
      {showReminderCalendar && (
        <div className="pixel-calendar-popup">
          <div className="pixel-popup-header">
            <h3 className="pixel-calendar-title">Set a Reminder</h3>
            <button
              className="pixel-popup-close"
              onClick={() => setShowReminderCalendar(false)}
            >
              ×
            </button>
          </div>

          {/* Task Selection */}
          <div className="pixel-calendar-section">
            <p className="pixel-calendar-label">Task Type:</p>
            <div className="pixel-task-options">
              <button
                className={`pixel-task-btn ${selectedTask === 'water' ? 'active' : ''}`}
                onClick={() => setSelectedTask('water')}
              >
                <span className="pixel-task-icon">💧</span> Water
              </button>
              <button
                className={`pixel-task-btn ${selectedTask === 'fertilise' ? 'active' : ''}`}
                onClick={() => setSelectedTask('fertilise')}
              >
                <span className="pixel-task-icon">🌱</span> Fertilise
              </button>
              <button
                className={`pixel-task-btn ${selectedTask === 'prune' ? 'active' : ''}`}
                onClick={() => setSelectedTask('prune')}
              >
                <span className="pixel-task-icon">✂️</span> Prune
              </button>
              {/* <button
                className={`pixel-task-btn ${selectedTask === 'custom' ? 'active' : ''}`}
                onClick={() => setSelectedTask('custom')}
              >
                <span className="pixel-task-icon">➕</span> Custom
              </button> */}
            </div>

            {/* Show text input for custom task */}
            {/* {selectedTask === 'custom' && (
              <input
                type="text"
                className="pixel-custom-task-input"
                placeholder="Enter custom task..."
                value={customTaskText}
                onChange={(e) => setCustomTaskText(e.target.value)}
              />
            )} */}
          </div>

          {/* Simplified Date Selection with Slider */}
          <div className="pixel-calendar-section">
            <div className="pixel-date-label-row">
              <p className="pixel-calendar-label">Select Date:</p>
              <button
                className="pixel-recommended-btn"
                onClick={useRecommendedSettings}
              >
                Use Recommended
              </button>
            </div>

            <div className="pixel-date-slider-container">
              <input
                type="range"
                min="1"
                max="30"
                value={daysFromNow}
                onChange={(e) => setDaysFromNow(parseInt(e.target.value, 10))}
                className="pixel-date-slider"
              />
              <div className="pixel-date-display">
                {formatDateDisplay(getDateFromSlider(daysFromNow))}
                <span className="pixel-days-indicator">({daysFromNow} days from now)</span>
              </div>
            </div>
          </div>

          {/* Simplified Time Selection with just a Slider */}
          <div className="pixel-calendar-section">
            <p className="pixel-calendar-label">Select Time:</p>

            <div className="pixel-time-slider-container">
              <input
                type="range"
                min="0"
                max="23"
                value={reminderHour}
                onChange={handleHourChange}
                className="pixel-time-slider"
              />
              <div className="pixel-time-display">
                {formatTimeDisplay(reminderHour)}
              </div>
            </div>

          </div>

          {/* Action Buttons - REVERSED again as requested */}
          <div className="pixel-calendar-buttons-row">
            <button
              className="pixel-calendar-button pixel-calendar-button-cancel"
              onClick={() => setShowReminderCalendar(false)}
            >
              Cancel
            </button>
            <button
              className="pixel-calendar-button pixel-calendar-button-confirm"
              onClick={handleSetReminder}
            >
              Set Reminder
            </button>
          </div>

          {hasReminder && (
            <button
              className="pixel-calendar-button pixel-calendar-button-clear"
              onClick={handleClearReminders}
            >
              Clear All Reminders
            </button>
          )}
        </div>
      )}

      {/* Enhanced Stage Detail Popup with Images from API */}
      {selectedStage && (
        <div className="pixel-stage-overlay">
          <div className="pixel-stage-modal">
            <div className="pixel-popup-header">
              <span className="pixel-popup-title">{selectedStage.name} Stage</span>
              <button className="pixel-popup-close" onClick={() => setSelectedStage(null)}>×</button>
            </div>

            {/* Add image from API if available - using the StageImageThumbnail */}
            {selectedStage.image_url && (
              <StageImageThumbnail
                imageUrl={selectedStage.image_url}
                altText={selectedStage.name}
                onClick={() => setEnlargedImage(selectedStage.image_url)}
              />
            )}

            <div className="pixel-popup-description">
              {selectedStage.description}
            </div>
            <div className="pixel-popup-care">
              <div className="pixel-care-title">Care Instructions:</div>
              <div className="pixel-care-instructions">{selectedStage.careInstructions}</div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal for full-size viewing */}
      {enlargedImage && (
        <ImageModal
          imageUrl={enlargedImage}
          onClose={() => setEnlargedImage(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && plantToDelete && (
        <div className="pixel-confirmation-overlay">
          <div className="pixel-confirmation-dialog">
            <div className="pixel-popup-header">
              <h3 className="pixel-confirmation-title">Confirm Deletion</h3>
              <button
                className="pixel-popup-close"
                onClick={handleCancelDelete}
              >
                ×
              </button>
            </div>

            <div className="pixel-confirmation-content">
              <p>Are you sure you want to remove: </p>
              <p className="pixel-plant-name">{plantToDelete.type}</p>
            </div>

            <div className="pixel-confirmation-buttons">
              <button
                className="pixel-confirmation-button pixel-confirmation-delete"
                onClick={handleConfirmDelete}
              >
                Delete Plant
              </button>
              <button
                className="pixel-confirmation-button pixel-confirmation-cancel"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual restart tour button */}
      {/* <button 
        className="pixel-tour-button" 
        onClick={() => {
          // When manually starting the tour, we need to clear the "Don't show again" cookie
          // so that the tour can be shown again even if user previously checked "Don't show again"
          // const cookieName = "gardenTourComplete";
          // document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          
          // Then start the tour
          startGardenTour();
        }}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          background: '#4caf50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '8px 12px',
          cursor: 'pointer',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
        }}
      >
        Start Garden Tour
      </button> */}
    </div>
  );
};

export default PixelGarden;